<template>
    <div class="kanban-contact-list-container">
        <!-- Toolbar -->
        <div class="kanban-toolbar">
            <div class="toolbar-left">
                <div class="filter-info">
                    <i class="material-icons filter-icon">{{ filterIcon }}</i>
                    <span class="filter-name">{{ filterName }}</span>
                </div>
                
                <!-- State Selector (Table gibi) -->
                <div class="state-selector-wrapper">
                    <a-tooltip
                        placement="top"
                        :title="`${$t('chat.tableContactList.chatStatus')}: ${getStatusTitle(effectiveState)}`"
                        :mouse-enter-delay="0.4"
                    >
                        <MinimalPopoverSelect
                            :value="effectiveState"
                            :options="stateOptions"
                            class="state-selector"
                            :icon="'layers'"
                            :show-dropdown-arrow="false"
                            button-class="chip"
                            @change="onStateChange"
                        />
                    </a-tooltip>
                </div>
                
                <!-- Filtre Ekle Butonu -->
                <SupsisButton @click="toggleFilterPanel" class="filter-toggle-btn">
                    <i class="material-icons">{{ showFilterPanel ? 'filter_list_off' : 'filter_list' }}</i>
                    {{ showFilterPanel ? $t('chat.kanbanContactList.filterHide') : $t('chat.kanbanContactList.filterAdd') }}
                </SupsisButton>
            </div>
            <div class="toolbar-center">
                <div class="group-selector-container">
                    <span class="selector-label">{{ $t('chat.tableContactList.grouping') }}:</span>
                    <KanbanGroupSelector v-model="selectedGroupField" @change="handleGroupFieldChange" />
                </div>
                
                <!-- Broadcast Selector - Sadece Broadcast Durumu modunda göster -->
                <div v-if="selectedGroupField === 'lastBroadcastStatus'" class="broadcast-selector-container">
                    <span class="selector-label">{{ $t('chat.kanbanContactList.broadcast') }}:</span>
                    <div class="broadcast-selector-wrapper">
                    <a-popover
                        v-model="broadcastPopoverVisible"
                        trigger="click"
                        placement="bottomLeft"
                        overlay-class-name="broadcast-popover"
                    >
                        <div class="broadcast-input-wrapper">
                            <input
                                v-model="broadcastSearchText"
                                type="text"
                                class="broadcast-input"
                                :placeholder="selectedBroadcast ? selectedBroadcast.title : $t('chat.kanbanContactList.broadcastSelectPlaceholder')"
                                @focus="handleBroadcastInputFocus"
                                @input="handleBroadcastSearchInput"
                            />
                            <span 
                                v-if="selectedBroadcast" 
                                class="material-icons clear-icon"
                                @click.stop="clearBroadcastSelection"
                            >
                                close
                            </span>
                            <span class="material-icons dropdown-icon">arrow_drop_down</span>
                        </div>
                        
                        <template slot="content">
                            <div class="broadcast-popover-content">
                                <div v-if="broadcastSearching" class="broadcast-loading">
                                    <a-spin size="small" />
                                    <span>{{ $t('chat.kanbanContactList.loading') }}</span>
                                </div>
                                
                                <div v-else-if="filteredBroadcastList.length === 0" class="broadcast-empty">
                                    <span class="material-icons">info</span>
                                    <span>{{ $t('chat.kanbanContactList.broadcastNotFound') }}</span>
                                </div>
                                
                                <div v-else class="broadcast-table">
                                    <div class="broadcast-table-header">
                                        <div class="col-title">{{ $t('chat.kanbanContactList.broadcastTitle') }}</div>
                                        <div class="col-date">{{ $t('chat.kanbanContactList.broadcastDate') }}</div>
                                        <div class="col-status">{{ $t('chat.kanbanContactList.broadcastStatus') }}</div>
                                        <div class="col-progress">{{ $t('chat.kanbanContactList.broadcastProgress') }}</div>
                                    </div>
                                    <div class="broadcast-table-body">
                                        <div
                                            v-for="broadcast in filteredBroadcastList"
                                            :key="broadcast._id"
                                            class="broadcast-row"
                                            :class="{ selected: selectedBroadcast && selectedBroadcast._id === broadcast._id }"
                                            @click="selectBroadcast(broadcast)"
                                        >
                                            <div class="col-title">
                                                <span class="broadcast-title" :title="broadcast.title">
                                                    {{ broadcast.title }}
                                                </span>
                                            </div>
                                            <div class="col-date">
                                                {{ formatBroadcastDate(broadcast.createdAt) }}
                                            </div>
                                            <div class="col-status">
                                                <span class="status-badge" :class="`status-${broadcast.status}`">
                                                    {{ getStatusLabel(broadcast.status) }}
                                                </span>
                                            </div>
                                            <div class="col-progress">
                                                <div class="progress-info">
                                                    <span class="progress-text">{{ getBroadcastProgress(broadcast) }}</span>
                                                    <div class="progress-bar-container">
                                                        <div 
                                                            class="progress-bar-fill"
                                                            :style="{ width: getBroadcastProgressPercent(broadcast) + '%' }"
                                                        ></div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </a-popover>
                    </div>
                </div>
            </div>
            <div class="toolbar-right">
                <SupsisButton @click="handleRefresh" :disabled="refreshDisabled" class="refresh-btn">
                    <i class="material-icons" :class="{ 'rotating': isRefreshing }">refresh</i>
                    {{ $t('chat.kanbanContactList.refresh') }}
                </SupsisButton>
            </div>
        </div>

        <!-- Filter Panel -->
        <div v-if="showFilterPanel" class="kanban-filter-panel">
            <FiltersSection 
                :filters="filters" 
                :state="state" 
                :sort-disabled="true"
                @filters-changed="onFiltersChanged" 
            />
        </div>

        <!-- Kanban Board -->
        <div class="kanban-board-wrapper" ref="kanbanWrapper">
            <div class="kanban-board" :style="{ minWidth: boardMinWidth }">
                <KanbanColumn
                    v-for="(group, index) in groupValues"
                    :key="group.value"
                    :group-value="group.value"
                    :group-label="group.label"
                    :color="getColumnColor(group.value, index)"
                    :contacts="columnContacts[group.value] || []"
                    :is-collapsed="collapsedColumns.has(group.value)"
                    :is-loading="columnLoading[group.value]"
                    :has-more="columnHasMore[group.value]"
                    :total-count="group.count"
                    :selected-contact-id="selectedContactId"
                    :filter-change-key="filterChangeKey"
                    :current-time="currentTime"
                    @toggle-column="toggleColumn"
                    @initial-load="handleInitialLoad"
                    @load-more="handleLoadMore(group.value)"
                    @contact-click="handleContactClick"
                />
            </div>
        </div>

        <!-- Loading Overlay -->
        <div v-if="initialLoading" class="loading-overlay">
            <div class="spinner-large"></div>
            <div class="loading-text">{{ $t('chat.kanbanContactList.kanbanLoading') }}</div>
        </div>
    </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import { Tooltip, Popover, Spin } from 'ant-design-vue';
import KanbanGroupSelector from './KanbanGroupSelector.vue';
import KanbanColumn from './KanbanColumn.vue';
import MinimalPopoverSelect from './MinimalPopoverSelect.vue';
import FiltersSection from './FiltersSection.vue';
import SupsisButton from '@/views/Pages/Settings/components/SupsisButton.vue';
import { getColorForGroup } from '../../../utils/kanbanColors';
import { maskContactInfo, isMaskContactInfo } from '../../../utils/utils';
import languages from '../../../language/languages.json';
import WHATSAPP_API from '@/api/whatsapp';
import _ from 'lodash';

export default {
    name: 'KanbanContactList',
    components: {
        KanbanGroupSelector,
        KanbanColumn,
        MinimalPopoverSelect,
        FiltersSection,
        SupsisButton,
        'a-tooltip': Tooltip,
        'a-popover': Popover,
        'a-spin': Spin,
    },
    props: {
        filterId: {
            type: String,
            default: null,
        },
    },
    data() {
        // URL'den veya localStorage'dan son seçili field'ı oku
        const urlField = this.$route.query.field;
        const savedGroupField = localStorage.getItem('kanban_selected_group_field');
        
        return {
            selectedGroupField: urlField || savedGroupField || 'answerStatus',
            filters: [],
            showFilterPanel: false,
            groupValues: [],
            collapsedColumns: new Set(),
            columnContacts: {},
            columnLoading: {},
            columnNextTokens: {},
            columnHasMore: {},
            filterChangeKey: 0,
            initialLoading: false,
            currentTime: Date.now(),
            timeUpdateInterval: null,
            lastRefreshTime: 0,
            isRefreshing: false,
            refreshDisabled: false,
            selectedBroadcast: null,
            broadcastList: [],
            broadcastSearching: false,
            broadcastSearchDebounce: null,
            broadcastPopoverVisible: false,
            broadcastSearchText: '',
        };
    },
    computed: {
        ...mapState({
            contactFilters: (state) => state.contact.filters,
            tags: (state) => state.tag.tags.data,
            subjects: (state) => state.subject.subjects?.data || [],
            channels: (state) => state.channel.channels || [],
            users: (state) => state.user.users?.data || [],
            selectedContact: (state) => state.contact.selectedContact,
            state: (state) => state.contact.selectedState,
        }),
        ...mapGetters({
            selectedFilter: 'contact/selectedFilter',
        }),
        filter() {
            return this.contactFilters.find((filter) => filter.id === this.filterId);
        },
        hasLastConversationStatusFilter() {
            if (!this.filter || !this.filter.filters) return false;
            return this.filter.filters.some((f) => f.field === "lastConversationStatus" && f.value);
        },
        effectiveState() {
            // lastConversationStatus filtresi varsa her zaman "all" döndür ama store'u değiştirme
            if (this.hasLastConversationStatusFilter) {
                return "all";
            }
            return this.state;
        },
        stateOptions() {
            // lastConversationStatus filtresi varsa sadece "all" seçeneği göster
            if (this.hasLastConversationStatusFilter) {
                return [{ label: this.$t('chat.tableContactList.statusAll'), value: 'all', icon: 'layers' }];
            }
            return [
                { label: this.$t('chat.tableContactList.statusAll'), value: 'all', icon: 'layers' },
                { label: this.$t('chat.tableContactList.statusOpen'), value: 'open', icon: 'lock_open' },
                { label: this.$t('chat.tableContactList.statusClosed'), value: 'closed', icon: 'lock' },
            ];
        },
        filterIcon() {
            return this.filter?.icon || 'filter_list';
        },
        filterName() {
            return this.filter?.name || this.$t('chat.tableContactList.allContacts');
        },
        selectedContactId() {
            return this.selectedContact?._id || null;
        },
        boardMinWidth() {
            let width = 0;
            this.groupValues.forEach((group) => {
                const isCollapsed = this.collapsedColumns.has(group.value);
                width += isCollapsed ? 36 + 16 : 320 + 16;
            });
            return `${width}px`;
        },
        filteredBroadcastList() {
            if (!this.broadcastSearchText || this.broadcastSearchText.trim() === '') {
                return this.broadcastList;
            }
            const searchLower = this.broadcastSearchText.toLowerCase().trim();
            return this.broadcastList.filter(b => 
                b.title && b.title.toLowerCase().includes(searchLower)
            );
        },
    
    },
    watch: {
        filterId: {
            immediate: true,
            handler(newVal, oldVal) {
                if (newVal && newVal !== oldVal) {
                    this.initFilters();
                    this.resetState();
                    this.loadGroupValues();
                }
            },
        },
        selectedGroupField: {
            immediate: true,
            handler(newVal, oldVal) {
                if (newVal && newVal !== oldVal) {
                    this.updateUrlQueryParams();
                }
                
                if (newVal === 'lastBroadcastStatus') {
                    const broadcastId = this.$route.query.broadcastId;
                    this.loadBroadcastList('').then(() => {
                        if (broadcastId && this.broadcastList.length > 0) {
                            const broadcast = this.broadcastList.find(b => b._id === broadcastId);
                            if (broadcast) {
                                this.selectedBroadcast = broadcast;
                                this.updateBroadcastFilter(broadcast.title);
                            }
                        }
                    });
                } else {
                    this.selectedBroadcast = null;
                    this.broadcastList = [];
                    this.broadcastSearchText = '';
                    this.filters = this.filters.filter(f => f.field !== 'lastTriggerState');
                    this.filters = [...this.filters];
                    this.resetState();
                    const query = Object.assign({}, this.$route.query);
                    delete query.broadcastId;
                    this.$router.replace({ query }).catch(() => {});
                }
            },
        },
        '$route.query.field': {
            immediate: true,
            handler(newVal) {
                if (newVal && newVal !== this.selectedGroupField) {
                    this.selectedGroupField = newVal;
                }
            },
        },
    },
    mounted() {
        this.timeUpdateInterval = setInterval(() => {
            this.currentTime = Date.now();
        }, 30000); // Her 30 saniyede bir güncelle
        
        this.handleBroadcastSelectionFromBreadcrumb = (event) => {
            const broadcastData = event.detail;
            if (broadcastData && this.selectedGroupField === 'lastBroadcastStatus') {
                this.broadcastList.push(broadcastData);
                this.selectBroadcast(broadcastData);
            }
        };
        
        window.addEventListener('select-broadcast-from-breadcrumb', this.handleBroadcastSelectionFromBreadcrumb);
        
        // filterId watch'ı immediate: true olduğu için mounted'da tekrar çağırmaya gerek yok
        // if (this.filterId) {
        //     this.initFilters();
        //     this.loadGroupValues();
        // }
    },
    beforeDestroy() {
        if (this.timeUpdateInterval) {
            clearInterval(this.timeUpdateInterval);
        }
        
        if (this.handleBroadcastSelectionFromBreadcrumb) {
            window.removeEventListener('select-broadcast-from-breadcrumb', this.handleBroadcastSelectionFromBreadcrumb);
        }
    },
    methods: {
        isMaskContactInfo,
        maskField(key, val) {
            return isMaskContactInfo(key) ? maskContactInfo(val) : val;
        },
        ...mapActions({
            getFilteredContacts: 'contact/getFilteredContacts',
            getContactListGroups: 'contact/getContactListGroups',
            setSelectedContact: 'contact/setSelectedContact',
            fetchContact: 'contact/fetchContact',
            getContactById: 'contact/getContactById',
            addNewWindow: 'app/addNewWindow',
            setSelectedState: 'contact/setSelectedState',
        }),
        refreshDisabled() {
            if (this.isRefreshing || this.initialLoading) return true;
            const now = Date.now();
            const timeSinceLastRefresh = now - this.lastRefreshTime;
            return timeSinceLastRefresh < 3000;
        },
        initFilters() {
            this.filters = [...(_.get(this.filter, 'filters') || [])];
        },
        resetState() {
            this.groupValues = [];
            this.columnContacts = {};
            this.columnLoading = {};
            this.columnNextTokens = {};
            this.columnHasMore = {};
            this.collapsedColumns = new Set();
            this.filterChangeKey++;
        },
        getStatusTitle(value) {
            if (value === 'all') return this.$t('chat.tableContactList.statusAll');
            if (value === 'open') return this.$t('chat.tableContactList.statusOpen');
            if (value === 'closed') return this.$t('chat.tableContactList.statusClosed');
            return this.$t('chat.tableContactList.statusUnknown');
        },
        toggleFilterPanel() {
            this.showFilterPanel = !this.showFilterPanel;
        },
        onFiltersChanged(payload) {
            this.filters = _.cloneDeep(payload.filters || []);
            // lastConversationStatus filtresi varsa store'u değiştirme
            if (payload.state && !this.hasLastConversationStatusFilter) {
                this.setSelectedState(payload.state);
            }
            this.resetState();
            this.loadGroupValues();
        },
        onStateChange(value) {
            // lastConversationStatus filtresi varsa store'u değiştirme, sadece yeniden yükle
            if (this.hasLastConversationStatusFilter) {
                this.resetState();
                this.loadGroupValues();
                return;
            }
            this.setSelectedState(value);
            this.resetState();
            this.loadGroupValues();
        },
        async loadGroupValues() {
            try {
                this.initialLoading = true;
                
                const params = {
                    groupField: this.selectedGroupField,
                    statusState: this.effectiveState,
                    filters: this.filters.filter((filter) => {
                        return filter.value !== null && filter.value !== undefined && filter.value !== '';
                    }),
                };
                
                const response = await this.getContactListGroups(params);
                
                // Backend'den gelen count'ları map'e dönüştür
                const countMap = {};
                (response.groups || []).forEach((group) => {
                    const key = group._id === null ? 'null' : group._id;
                    countMap[key] = group.count || 0;
                });
                
                // Bilinen field'lar için frontend'den tüm olası değerleri oluştur
                let allGroups = [];
                
                if (this.selectedGroupField === 'tags') {
                    // Tüm tag'leri dahil et
                    allGroups = this.tags.map((tag) => ({
                        label: tag.name,
                        value: tag._id,
                        count: countMap[tag._id] || 0,
                    }));
                } else if (this.selectedGroupField === 'owner') {
                    // Tüm user'ları dahil et + Sahipsizler + Backend'den gelen silinmiş kullanıcılar
                    const knownUserIds = this.users.map((u) => u._id);
                    allGroups = this.users.map((user) => ({
                        label: user.name || user.email,
                        value: user._id,
                        count: countMap[user._id] || 0,
                    }));
                    
                    // Backend'den gelen ama store'da olmayan kullanıcılar (silinmiş)
                    (response.groups || []).forEach((group) => {
                        if (group._id && group._id !== 'no-owner' && !knownUserIds.includes(group._id)) {
                            allGroups.push({
                                label: this.$t('chat.kanbanContactList.deletedUser'),
                                value: group._id,
                                count: group.count || 0,
                            });
                        }
                    });
                    
                    // Sahipsizler grubunu ekle
                    allGroups.push({
                        label: this.$t('chat.kanbanContactList.unowned'),
                        value: 'no-owner',
                        count: countMap['no-owner'] || 0,
                    });
                } else if (this.selectedGroupField === 'users') {
                    // Tüm user'ları dahil et + Silinmiş kullanıcılar
                    const knownUserIds = this.users.map((u) => u._id);
                    allGroups = this.users.map((user) => ({
                        label: user.name || user.email,
                        value: user._id,
                        count: countMap[user._id] || 0,
                    }));
                    
                    // Backend'den gelen ama store'da olmayan kullanıcılar (silinmiş)
                    (response.groups || []).forEach((group) => {
                        if (group._id && group._id !== 'no-owner' && !knownUserIds.includes(group._id)) {
                            allGroups.push({
                                label: this.$t('chat.kanbanContactList.deletedUser'),
                                value: group._id,
                                count: group.count || 0,
                            });
                        }
                    });
                } else if (this.selectedGroupField === 'channel') {
                    // Sadece belirli tiplerdeki kanalları dahil et
                    const validChannelTypes = [
                        'whatsapp-qr',
                        'whatsapp-v2',
                        'whatsapp-business-api',
                        'whatsapp-unipile',
                        'telegram',
                        'telegram-unipile',
                        'instagram',
                        'messenger',
                        'meta-leads',
                        'linkedin-unipile',
                    ];
                    allGroups = this.channels
                        .filter((ch) => validChannelTypes.includes(ch.type))
                        .map((channel) => ({
                            label: channel.name,
                            value: channel._id,
                            count: countMap[channel._id] || 0,
                        }));
                } else if (this.selectedGroupField === 'department') {
                    // Tüm departmanları dahil et
                    allGroups = this.subjects.map((subject) => ({
                        label: subject.languageContent[this.$i18n.locale]?.name ||
                            subject.languageContent[Object.keys(subject.languageContent)[0]]?.name ||
                            subject._id,
                        value: subject._id,
                        count: countMap[subject._id] || 0,
                    }));
                } else if (this.selectedGroupField === 'answerStatus') {
                    // Sabit değerler
                    allGroups = [
                        { label: this.$t('chat.tableContactList.answerStatusAnswered'), value: 'answered', count: countMap['answered'] || 0 },
                        { label: this.$t('chat.tableContactList.answerStatusUnanswered'), value: 'unanswered', count: countMap['unanswered'] || 0 },
                    ];
                } else if (this.selectedGroupField === 'lastConversationStatus') {
                    // Sabit değerler
                    allGroups = [
                        { label: this.$t('chat.kanbanContactList.activeInChat'), value: 'active', count: countMap['active'] || 0 },
                        { label: this.$t('chat.kanbanContactList.newInQueue'), value: 'new', count: countMap['new'] || 0 },
                        { label: this.$t('chat.filtersSection.chatbot'), value: 'chatbot', count: countMap['chatbot'] || 0 },
                        { label: this.$t('chat.kanbanContactList.agentDown'), value: 'lost', count: countMap['lost'] || 0 },
                        { label: this.$t('chat.kanbanContactList.closed'), value: 'destroyed', count: countMap['destroyed'] || 0 },
                        { label: this.$t('chat.filterPanel.private'), value: 'private', count: countMap['private'] || 0 },
                    ];
                } else if (this.selectedGroupField === 'blockedSnapshot') {
                    // Boolean değerler
                    allGroups = [
                        { label: this.$t('chat.kanbanContactList.blocked'), value: 'true', count: countMap['true'] || 0 },
                        { label: this.$t('chat.kanbanContactList.unblocked'), value: 'false', count: countMap['false'] || 0 },
                    ];
                } else if (this.selectedGroupField === 'platform') {
                    // Sabit platform değerleri
                    const platforms = [
                        { key: 'supsis', label: this.$t('chat.tableContactList.platformSupsis') },
                        { key: 'email', label: this.$t('chat.tableContactList.platformEmail') },
                        { key: 'whatsapp', label: this.$t('chat.tableContactList.platformWhatsapp') },
                        { key: 'web', label: this.$t('chat.tableContactList.platformWeb') },
                        { key: 'mobile', label: this.$t('chat.kanbanContactList.platformMobile') },
                        { key: 'instagram', label: this.$t('chat.tableContactList.platformInstagram') },
                        { key: 'telegram', label: this.$t('chat.tableContactList.platformTelegram') },
                        { key: 'messenger', label: this.$t('chat.tableContactList.platformMessenger') },
                        { key: 'meta-leads', label: this.$t('chat.tableContactList.platformMetaLeads') },
                        { key: 'trendyol', label: this.$t('chat.tableContactList.platformTrendyol') },
                        { key: 'linkedin', label: this.$t('chat.tableContactList.platformLinkedin') },
                    ];
                    allGroups = platforms.map((p) => ({
                        label: p.label,
                        value: p.key,
                        count: countMap[p.key] || 0,
                    }));
                } else if (this.selectedGroupField === 'metaLeadStatus') {
                    // Meta Lead status sabit değerleri
                    const statuses = [
                        { key: 'new_lead', label: this.$t('chat.kanbanContactList.metaLeadNewLead') },
                        { key: 'initial_contact', label: this.$t('chat.kanbanContactList.metaLeadInitialContact') },
                        { key: 'needs_assessment', label: this.$t('chat.kanbanContactList.metaLeadNeedsAssessment') },
                        { key: 'proposal_sent', label: this.$t('chat.kanbanContactList.metaLeadProposalSent') },
                        { key: 'proposal_review', label: this.$t('chat.kanbanContactList.metaLeadProposalReview') },
                        { key: 'negotiation', label: this.$t('chat.kanbanContactList.metaLeadNegotiation') },
                        { key: 'decision_stage', label: this.$t('chat.kanbanContactList.metaLeadDecisionStage') },
                        { key: 'closure', label: this.$t('chat.kanbanContactList.metaLeadClosure') },
                        { key: 'follow_up', label: this.$t('chat.kanbanContactList.metaLeadFollowUp') },
                    ];
                    allGroups = statuses.map((s) => ({
                        label: s.label,
                        value: s.key,
                        count: countMap[s.key] || 0,
                    }));
                } else if (this.selectedGroupField === 'lastBroadcastStatus') {
                    // Broadcast status sabit değerleri (semantic sıralama: mesaj akış sırasına göre)
                    const statuses = [
                        { key: 'sent', label: this.$t('chat.kanbanContactList.broadcastSent') },
                        { key: 'delivered', label: this.$t('chat.kanbanContactList.broadcastDelivered') },
                        { key: 'read', label: this.$t('chat.kanbanContactList.broadcastRead') },
                        { key: 'failed', label: this.$t('chat.kanbanContactList.broadcastFailed') },
                        { key: 'edited', label: this.$t('chat.kanbanContactList.broadcastEdited') },
                        { key: 'deleted', label: this.$t('chat.kanbanContactList.broadcastDeleted') },
                    ];
                    allGroups = statuses.map((s) => ({
                        label: s.label,
                        value: s.key,
                        count: countMap[s.key] || 0,
                    }));
                } else if (this.selectedGroupField === 'lastTriggerType') {
                    // Trigger type sabit değerleri
                    const types = [
                        { key: 'api', label: this.$t('chat.kanbanContactList.triggerTypeApi') },
                        { key: 'broadcast', label: this.$t('chat.kanbanContactList.triggerTypeBroadcast') },
                        { key: 'automation', label: this.$t('chat.kanbanContactList.triggerTypeAutomation') },
                        { key: 'null', label: this.$t('chat.tableContactList.groupNone') },
                    ];
                    allGroups = types.map((t) => ({
                        label: t.label,
                        value: t.key,
                        count: countMap[t.key] || 0,
                    }));
                } else if (this.selectedGroupField === 'preferredLang') {
                    // Sabit 8 dil + Backend'den gelen diğer diller
                    const defaultLanguages = ['tr', 'en', 'de', 'fr', 'es', 'ar', 'ru'];
                    
                    // Dil label'ları languages.json'dan al
                    const langLabelMap = {};
                    languages.forEach((lang) => {
                        langLabelMap[lang.code] = lang.originalName || lang.label;
                    });
                    
                    // Default dilleri önce ekle
                    allGroups = defaultLanguages.map((langCode) => ({
                        label: langLabelMap[langCode] || langCode.toUpperCase(),
                        value: langCode,
                        count: countMap[langCode] || 0,
                    }));
                    
                    // Backend'den gelen ekstra dilleri ekle
                    (response.groups || []).forEach((group) => {
                        if (!defaultLanguages.includes(group._id)) {
                            allGroups.push({
                                label: langLabelMap[group._id] || group._id?.toUpperCase() || this.$t('chat.tableContactList.statusUnknown'),
                                value: group._id,
                                count: group.count || 0,
                            });
                        }
                    });
                } else if (this.selectedGroupField === 'metaLeadPlatform') {
                    // Meta platform değerleri (ig, fb gibi)
                    const knownPlatforms = [
                        { key: 'ig', label: this.$t('chat.kanbanContactList.platformIg') },
                        { key: 'fb', label: this.$t('chat.kanbanContactList.platformFb') },
                    ];
                    
                    const knownKeys = knownPlatforms.map((p) => p.key);
                    const allPlatforms = [...knownPlatforms];
                    
                    // Backend'den gelenleri de ekle
                    (response.groups || []).forEach((group) => {
                        if (!knownKeys.includes(group._id)) {
                            allPlatforms.push({
                                key: group._id,
                                label: group._id || this.$t('chat.tableContactList.statusUnknown'),
                            });
                        }
                    });
                    
                    allGroups = allPlatforms.map((p) => ({
                        label: p.label,
                        value: p.key,
                        count: countMap[p.key] || 0,
                    }));
                } else {
                    // Diğer field'lar için backend'den gelen değerleri kullan (dinamik değerler)
                    // metaLeadAdName, metaLeadCampaignName, country, city, browser vb.
                    allGroups = (response.groups || []).map((group) => ({
                                label: group._id || this.$t('chat.tableContactList.statusUnknown'),
                        value: group._id,
                        count: group.count || 0,
                    }));
                }
                
                this.groupValues = allGroups;
                
                // Otomatik olarak boş kolonları collapse et
                this.groupValues.forEach((group) => {
                    if (group.count === 0) {
                        this.collapsedColumns.add(group.value);
                    }
                });
                this.collapsedColumns = new Set(this.collapsedColumns);
                
            } catch (error) {
                console.error('Failed to load group values:', error);
                this.$message.error(this.$t('chat.kanbanContactList.groupsLoadError'));
            } finally {
                this.initialLoading = false;
            }
        },
        getGroupLabel(groupValue) {
            // Özel değerler
            if (groupValue === 'no-owner' || groupValue === 'null' || groupValue === null || groupValue === undefined) {
                if (this.selectedGroupField === 'owner') {
                    return this.$t('chat.kanbanContactList.unowned');
                }
                if (this.selectedGroupField === 'lastTriggerType') {
                    return this.$t('chat.tableContactList.groupNone');
                }
                return this.$t('chat.kanbanContactList.assigned');
            }
            
            // Özel etiketleme mantığı
            if (this.selectedGroupField === 'tags') {
                const tag = this.tags.find((t) => t._id === groupValue);
                return tag?.name || groupValue;
            } else if (this.selectedGroupField === 'department') {
                const subject = this.subjects.find((s) => s._id === groupValue);
                if (subject) {
                    return subject.languageContent[this.$i18n.locale]?.name ||
                        subject.languageContent[Object.keys(subject.languageContent)[0]]?.name ||
                        groupValue;
                }
            } else if (this.selectedGroupField === 'channel') {
                const channel = this.channels.find((c) => c._id === groupValue);
                return channel?.name || groupValue;
            } else if (this.selectedGroupField === 'owner' || this.selectedGroupField === 'users') {
                const user = this.users.find((u) => u._id === groupValue);
                return user?.name || user?.email || this.$t('chat.kanbanContactList.deletedUser');
            } else if (this.selectedGroupField === 'answerStatus') {
                return groupValue === 'answered' ? this.$t('chat.tableContactList.answerStatusAnswered') : this.$t('chat.tableContactList.answerStatusUnanswered');
            } else if (this.selectedGroupField === 'lastConversationStatus') {
                const statusLabels = {
                    active: this.$t('chat.kanbanContactList.activeInChat'),
                    chatbot: this.$t('chat.filtersSection.chatbot'),
                    new: this.$t('chat.kanbanContactList.newInQueue'),
                    lost: this.$t('chat.kanbanContactList.agentDown'),
                    destroyed: this.$t('chat.kanbanContactList.closed'),
                    private: this.$t('chat.filterPanel.private'),
                };
                return statusLabels[groupValue] || groupValue;
            } else if (this.selectedGroupField === 'blockedSnapshot') {
                return groupValue === 'true' || groupValue === true ? this.$t('chat.kanbanContactList.blocked') : this.$t('chat.kanbanContactList.unblocked');
            } else if (this.selectedGroupField === 'platform') {
                const platformLabels = {
                    supsis: this.$t('chat.tableContactList.platformSupsis'),
                    email: this.$t('chat.tableContactList.platformEmail'),
                    whatsapp: this.$t('chat.tableContactList.platformWhatsapp'),
                    web: this.$t('chat.tableContactList.platformWeb'),
                    mobile: this.$t('chat.kanbanContactList.platformMobile'),
                    instagram: this.$t('chat.tableContactList.platformInstagram'),
                    telegram: this.$t('chat.tableContactList.platformTelegram'),
                    messenger: this.$t('chat.tableContactList.platformMessenger'),
                    'meta-leads': this.$t('chat.tableContactList.platformMetaLeads'),
                    trendyol: this.$t('chat.tableContactList.platformTrendyol'),
                    linkedin: this.$t('chat.tableContactList.platformLinkedin'),
                };
                return platformLabels[groupValue] || groupValue;
            } else if (this.selectedGroupField === 'metaLeadStatus') {
                const statusLabels = {
                    new_lead: this.$t('chat.kanbanContactList.metaLeadNewLead'),
                    initial_contact: this.$t('chat.kanbanContactList.metaLeadInitialContact'),
                    needs_assessment: this.$t('chat.kanbanContactList.metaLeadNeedsAssessment'),
                    proposal_sent: this.$t('chat.kanbanContactList.metaLeadProposalSent'),
                    proposal_review: this.$t('chat.kanbanContactList.metaLeadProposalReview'),
                    negotiation: this.$t('chat.kanbanContactList.metaLeadNegotiation'),
                    decision_stage: this.$t('chat.kanbanContactList.metaLeadDecisionStage'),
                    closure: this.$t('chat.kanbanContactList.metaLeadClosure'),
                    follow_up: this.$t('chat.kanbanContactList.metaLeadFollowUp'),
                };
                return statusLabels[groupValue] || groupValue;
            } else if (this.selectedGroupField === 'lastBroadcastStatus') {
                const statusLabels = {
                    read: this.$t('chat.kanbanContactList.broadcastRead'),
                    sent: this.$t('chat.kanbanContactList.broadcastSent'),
                    delivered: this.$t('chat.kanbanContactList.broadcastDelivered'),
                    deleted: this.$t('chat.kanbanContactList.broadcastDeleted'),
                    edited: this.$t('chat.kanbanContactList.broadcastEdited'),
                    failed: this.$t('chat.kanbanContactList.broadcastFailed'),
                };
                return statusLabels[groupValue] || groupValue;
            } else if (this.selectedGroupField === 'lastTriggerType') {
                const typeLabels = {
                    api: this.$t('chat.kanbanContactList.triggerTypeApi'),
                    broadcast: this.$t('chat.kanbanContactList.triggerTypeBroadcast'),
                    automation: this.$t('chat.kanbanContactList.triggerTypeAutomation'),
                };
                return typeLabels[groupValue] || groupValue;
            } else if (this.selectedGroupField === 'preferredLang') {
                const langLabels = {
                    tr: 'Türkçe',
                    en: 'English',
                    de: 'Deutsch',
                    fr: 'Français',
                    es: 'Español',
                    ar: 'العربية',
                    ru: 'Русский',
                };
                return langLabels[groupValue] || groupValue;
            } else if (this.selectedGroupField === 'metaLeadPlatform') {
                const platformLabels = {
                    ig: this.$t('chat.kanbanContactList.platformIg'),
                    fb: this.$t('chat.kanbanContactList.platformFb'),
                };
                return platformLabels[groupValue] || groupValue;
            }
            
            return groupValue || this.$t('chat.tableContactList.statusUnknown');
        },
        getColumnColor(groupValue, index) {
            return getColorForGroup(this.selectedGroupField, groupValue, index, this.groupValues.length);
        },
        toggleColumn(groupValue) {
            if (this.collapsedColumns.has(groupValue)) {
                this.collapsedColumns.delete(groupValue);
            } else {
                this.collapsedColumns.add(groupValue);
            }
            this.collapsedColumns = new Set(this.collapsedColumns);
        },
        async handleInitialLoad(groupValue) {
            await this.loadColumnData(groupValue, false);
        },
        async handleLoadMore(groupValue) {
            await this.loadColumnData(groupValue, true);
        },
        async loadColumnData(groupValue, append = false) {
            if (this.columnLoading[groupValue]) {
                return;
            }
            
            this.$set(this.columnLoading, groupValue, true);
            
            if (!append) {
                this.$set(this.columnContacts, groupValue, []);
                this.$set(this.columnNextTokens, groupValue, null);
            }
            
            try {
                // 'no-owner' string değerini gerçek null'a çevir
                const actualGroupValue = groupValue === 'no-owner' ? null : groupValue;
                
                const payload = {
                    cursor: {
                        limit: 20,
                    },
                    state: this.effectiveState,
                    metadata: {
                        source: 'KanbanContactList.loadColumnData',
                        withoutCount: 'true',
                    },
                    group: {
                        field: this.selectedGroupField,
                        value: actualGroupValue,
                    },
                };
                
                // Pagination token ekle (append modunda)
                if (append && this.columnNextTokens[groupValue]) {
                    payload.cursor.nextToken = this.columnNextTokens[groupValue];
                }
                
                if (this.filterId) {
                    payload.inbox = {
                        type: this.filter?.type || 'system',
                        id: this.filterId,
                    };
                }
                
                if (this.filters.length > 0) {
                    payload.filters = this.filters.filter((filter) => {
                        return filter.value !== null && filter.value !== undefined && filter.value !== '';
                    });
                }
                
                const response = await this.getFilteredContacts(payload);
                
                const contacts = response.contacts || [];
                
                if (append) {
                    this.$set(
                        this.columnContacts,
                        groupValue,
                        [...(this.columnContacts[groupValue] || []), ...contacts]
                    );
                } else {
                    this.$set(this.columnContacts, groupValue, contacts);
                }
                
                // Next token'ı sakla
                this.$set(this.columnNextTokens, groupValue, response.cursor?.nextToken || null);
                this.$set(this.columnHasMore, groupValue, !!response.cursor?.nextToken);
                
            } catch (error) {
                console.error(`Failed to load column ${groupValue}:`, error);
                this.$message.error(this.$t('chat.kanbanContactList.dataLoadError'));
            } finally {
                this.$set(this.columnLoading, groupValue, false);
            }
        },
        async loadBroadcastList(search = '') {
            try {
                this.broadcastSearching = true;
                const broadcasts = await WHATSAPP_API.BROADCAST.getBroadcastList(50, search);
                this.broadcastList = broadcasts || [];
            } catch (error) {
                console.error('Failed to load broadcasts:', error);
                this.$message.error(this.$t('chat.kanbanContactList.broadcastsLoadError'));
                this.broadcastList = [];
            } finally {
                this.broadcastSearching = false;
            }
        },
        handleBroadcastInputFocus() {
            this.broadcastPopoverVisible = true;
            if (this.broadcastList.length === 0) {
                this.loadBroadcastList('');
            }
        },
        handleBroadcastSearchInput() {
            if (this.broadcastSearchDebounce) {
                clearTimeout(this.broadcastSearchDebounce);
            }
            this.broadcastSearchDebounce = setTimeout(() => {
                this.loadBroadcastList(this.broadcastSearchText);
            }, 300);
        },
        selectBroadcast(broadcast) {
            this.selectedBroadcast = broadcast;
            this.broadcastSearchText = '';
            this.broadcastPopoverVisible = false;
            this.updateBroadcastFilter(broadcast.title);
            this.updateUrlQueryParams();
        },
        clearBroadcastSelection(e) {
            e.stopPropagation();
            this.selectedBroadcast = null;
            this.broadcastSearchText = '';
            
            const existingFilterIndex = this.filters.findIndex(f => f.field === 'lastTriggerState');
            if (existingFilterIndex !== -1) {
                this.filters.splice(existingFilterIndex, 1);
                this.filters = [...this.filters];
                this.resetState();
                this.loadGroupValues();
            }
            
            const query = Object.assign({}, this.$route.query);
            delete query.broadcastId;
            this.$router.replace({ query }).catch(() => {});
        },
        updateUrlQueryParams() {
            const query = Object.assign({}, this.$route.query);
            query.field = this.selectedGroupField;
            if (this.selectedBroadcast && this.selectedGroupField === 'lastBroadcastStatus') {
                query.broadcastId = this.selectedBroadcast._id;
            } else {
                delete query.broadcastId;
            }
            this.$router.replace({ query }).catch(() => {});
        },
        updateBroadcastFilter(broadcastTitle) {
            const existingFilterIndex = this.filters.findIndex(f => f.field === 'lastTriggerState');
            
            const newFilter = {
                field: 'lastTriggerState',
                value: {
                    selectValue: 'broadcast',
                    inputValue: broadcastTitle,
                },
            };
            
            if (existingFilterIndex !== -1) {
                this.filters.splice(existingFilterIndex, 1, newFilter);
            } else {
                this.filters.push(newFilter);
            }
            
            this.filters = [...this.filters];
            this.resetState();
            this.loadGroupValues();
        },
        removeBroadcastFilter() {
            const existingFilterIndex = this.filters.findIndex(f => f.field === 'lastTriggerState');
            if (existingFilterIndex !== -1) {
                this.filters.splice(existingFilterIndex, 1);
                this.filters = [...this.filters];
                // resetState ve loadGroupValues handleGroupFieldChange'de çağrılacak, burada gereksiz
            }
        },
        formatBroadcastDate(dateString) {
            const date = this.$moment(dateString);
            const now = this.$moment();
            const diffDays = now.diff(date, 'days');
            
            if (diffDays === 0) {
                return date.format('HH:mm');
            } else if (diffDays < 7) {
                return date.format('dd, HH:mm');
            } else {
                return date.format('DD MMM');
            }
        },
        getStatusLabel(status) {
            const statusLabels = {
                created: this.$t('chat.kanbanContactList.broadcastStatusCreated'),
                scheduled: this.$t('chat.kanbanContactList.broadcastStatusScheduled'),
                queued: this.$t('chat.kanbanContactList.broadcastStatusQueued'),
                running: this.$t('chat.kanbanContactList.broadcastStatusRunning'),
                completed: this.$t('chat.kanbanContactList.broadcastStatusCompleted'),
                failed: this.$t('chat.kanbanContactList.broadcastStatusFailed'),
                cancelled: this.$t('chat.kanbanContactList.broadcastStatusCancelled'),
            };
            return statusLabels[status] || status;
        },
        getBroadcastProgress(broadcast) {
            if (!broadcast.total) return '0/0';
            const sent = (broadcast.sent || 0) + (broadcast.failed || 0);
            return `${sent}/${broadcast.total}`;
        },
        getBroadcastProgressPercent(broadcast) {
            if (!broadcast.total) return 0;
            const sent = (broadcast.sent || 0) + (broadcast.failed || 0);
            return Math.round((sent / broadcast.total) * 100);
        },
        handleGroupFieldChange: _.debounce(function() {
            // localStorage'a kaydet
            localStorage.setItem('kanban_selected_group_field', this.selectedGroupField);
            
            // Eğer broadcast durumundan başka bir field'a geçildiyse broadcast filtresini temizle
            if (this.selectedGroupField !== 'lastBroadcastStatus') {
                this.removeBroadcastFilter();
                this.selectedBroadcast = null;
                this.broadcastSearchText = '';
            }
            
            this.resetState();
            this.loadGroupValues();
        }, 300),
        async handleRefresh() {
            const now = Date.now();
            const timeSinceLastRefresh = now - this.lastRefreshTime;
            
            if (timeSinceLastRefresh < 3000 || this.isRefreshing || this.initialLoading) {
                return;
            }
            
            this.isRefreshing = true;
            this.lastRefreshTime = now;
            this.refreshDisabled = true;
            try {
                this.resetState();
                await this.loadGroupValues();
                this.$message.success(this.$t('chat.kanbanContactList.dataRefreshed'));
            } catch (error) {
                this.$message.error(this.$t('chat.kanbanContactList.refreshError'));
            } finally {
                // 500ms sonra animation bitsin
                setTimeout(() => {
                    this.isRefreshing = false;
                }, 500);
                setTimeout(() => {
                    this.refreshDisabled = false;
                }, 3000);
            }
        },
        async handleContactClick(contact) {
            let fullContact = await this.getContactById(contact._id);
            if (!fullContact?.inited) {
                fullContact = await this.fetchContact({
                    contactId: contact._id,
                    ignoreSource: 'KanbanContactList.handleContactClick',
                    viewed: true,
                });
            }

            const windows = this.$store.state.app.windows;
            const windowKeys = Object.keys(windows);
            const lastWindowKey = windowKeys[windowKeys.length - 1];
            const lastWindow = windows[lastWindowKey];

            if (
                lastWindow &&
                lastWindow.type === 'grid-chat-window-contact' &&
                lastWindow.payload &&
                lastWindow.payload.contactId === fullContact._id
            ) {
                return;
            }

            this.addNewWindow({
                type: 'grid-chat-window-contact',
                title: fullContact.fullname || this.maskField('email', fullContact.email) || this.maskField('phone', fullContact.phone) || this.$t('chat.kanbanContactList.unknownContact'),
                autoClose: false,
                multiple: false,
                fullShow: true,
                footer: false,
                payload: {
                    contactId: fullContact._id,
                },
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.kanban-contact-list-container {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    position: relative;
    background: #f9fafb;
}

.kanban-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 24px;
    background: white;
    border-bottom: 1px solid #e5e7eb;
    flex-shrink: 0;
    gap: 16px;
}

.toolbar-left {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
}

.toolbar-center {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 20px;
}

.group-selector-container,
.broadcast-selector-container {
    display: flex;
    align-items: center;
    gap: 8px;
    
    .selector-label {
        font-size: 13px;
        font-weight: 500;
        color: #6b7280;
        white-space: nowrap;
    }
}

.broadcast-selector-wrapper {
    display: flex;
    align-items: center;
    
    .broadcast-input-wrapper {
        position: relative;
        display: flex;
        align-items: center;
        width: 250px;
        
        .broadcast-input {
            width: 100%;
            height: 36px;
            padding: 8px 60px 8px 12px;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            font-size: 13px;
            outline: none;
            transition: border-color 0.2s ease;
            
            &::placeholder {
                color: #9ca3af;
            }
            
            &:focus {
                border-color: #3b82f6;
            }
        }
        
        .clear-icon {
            position: absolute;
            right: 32px;
            font-size: 18px;
            color: #6b7280;
            cursor: pointer;
            padding: 4px;
            border-radius: 4px;
            transition: all 0.2s ease;
            
            &:hover {
                color: #1f2937;
                background: #f3f4f6;
            }
        }
        
        .dropdown-icon {
            position: absolute;
            right: 8px;
            font-size: 20px;
            color: #6b7280;
            pointer-events: none;
        }
    }
}

::v-deep .broadcast-popover {
    .ant-popover-inner {
        border-radius: 8px;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    }
    
    .ant-popover-inner-content {
        padding: 0;
    }
}

.broadcast-popover-content {
    width: 700px;
    max-height: 400px;
    
    .broadcast-loading,
    .broadcast-empty {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 40px 20px;
        color: #6b7280;
        font-size: 13px;
        
        .material-icons {
            font-size: 20px;
        }
    }
    
    .broadcast-table {
        display: flex;
        flex-direction: column;
        
        .broadcast-table-header {
            display: flex;
            background: #f9fafb;
            border-bottom: 2px solid #e5e7eb;
            padding: 12px 16px;
            font-size: 12px;
            font-weight: 600;
            color: #6b7280;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            
            .col-title {
                flex: 2;
                min-width: 0;
            }
            
            .col-date {
                flex: 0 0 100px;
            }
            
            .col-status {
                flex: 0 0 120px;
            }
            
            .col-progress {
                flex: 0 0 140px;
            }
        }
        
        .broadcast-table-body {
            max-height: 350px;
            overflow-y: auto;
            
            &::-webkit-scrollbar {
                width: 6px;
            }
            
            &::-webkit-scrollbar-track {
                background: #f3f4f6;
            }
            
            &::-webkit-scrollbar-thumb {
                background: #d1d5db;
                border-radius: 3px;
                
                &:hover {
                    background: #9ca3af;
                }
            }
            
            .broadcast-row {
                display: flex;
                padding: 12px 16px;
                border-bottom: 1px solid #f3f4f6;
                cursor: pointer;
                transition: background 0.15s ease;
                
                &:hover {
                    background: #f9fafb;
                }
                
                &.selected {
                    background: #eff6ff;
                    
                    .broadcast-title {
                        color: #1e40af;
                        font-weight: 500;
                    }
                }
                
                .col-title {
                    flex: 2;
                    min-width: 0;
                    display: flex;
                    align-items: center;
                    
                    .broadcast-title {
                        font-size: 13px;
                        color: #1f2937;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                    }
                }
                
                .col-date {
                    flex: 0 0 100px;
                    display: flex;
                    align-items: center;
                    font-size: 12px;
                    color: #6b7280;
                }
                
                .col-status {
                    flex: 0 0 120px;
                    display: flex;
                    align-items: center;
                    
                    .status-badge {
                        padding: 4px 8px;
                        border-radius: 4px;
                        font-size: 11px;
                        font-weight: 500;
                        white-space: nowrap;
                        
                        &.status-completed {
                            background: #d1fae5;
                            color: #065f46;
                        }
                        
                        &.status-running {
                            background: #dbeafe;
                            color: #1e40af;
                        }
                        
                        &.status-queued {
                            background: #e5e7eb;
                            color: #374151;
                        }
                        
                        &.status-scheduled {
                            background: #fef3c7;
                            color: #92400e;
                        }
                        
                        &.status-failed {
                            background: #fee2e2;
                            color: #991b1b;
                        }
                        
                        &.status-cancelled {
                            background: #fef3c7;
                            color: #92400e;
                        }
                        
                        &.status-created {
                            background: #dbeafe;
                            color: #1e40af;
                        }
                    }
                }
                
                .col-progress {
                    flex: 0 0 140px;
                    display: flex;
                    align-items: center;
                    
                    .progress-info {
                        display: flex;
                        flex-direction: column;
                        gap: 4px;
                        width: 100%;
                        
                        .progress-text {
                            font-size: 11px;
                            color: #6b7280;
                            font-weight: 500;
                        }
                        
                        .progress-bar-container {
                            width: 100%;
                            height: 6px;
                            background: #e5e7eb;
                            border-radius: 3px;
                            overflow: hidden;
                            
                            .progress-bar-fill {
                                height: 100%;
                                background: #3b82f6;
                                border-radius: 3px;
                                transition: width 0.3s ease;
                            }
                        }
                    }
                }
            }
        }
    }
}

.toolbar-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex: 1;
}

.filter-info {
    display: flex;
    align-items: center;
    gap: 10px;
}

.filter-icon {
    font-size: 24px;
    color: #6b7280;
}

.filter-name {
    font-size: 16px;
    font-weight: 600;
    color: #1f2937;
}

.state-selector-wrapper {
    flex-shrink: 0;
    
    .state-selector {
        ::v-deep .chip {
            display: inline-flex;
            align-items: center;
            gap: 0.4rem;
            padding: 0.35rem 0.7rem;
            border: 1px solid #c7d0d7;
            border-radius: 999px;
            background: #fff;
            color: #22272d;
            cursor: pointer;
            user-select: none;
            transition: background 0.15s ease, box-shadow 0.15s ease, transform 0.05s ease;
            font-weight: 600;

            &:hover {
                background: #f0f0f0;
                color: #22272d;

                .material-icons {
                    color: #22272d !important;
                }
            }

            .material-icons {
                font-size: 18px;
                line-height: 1;
                color: #22272d;
            }
        }
    }
}

.filter-toggle-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 32px;
    padding: 0 12px;
    font-size: 13px;
    font-weight: 500;
    
    .material-icons {
        font-size: 16px;
    }
}

.kanban-filter-panel {
    padding: 16px 24px;
    background: white;
    border-bottom: 1px solid #e5e7eb;
    flex-shrink: 0;
}

.refresh-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 32px;
    padding: 0 12px;
    font-size: 13px;
    font-weight: 500;
    
    .material-icons {
        font-size: 18px;
        transition: transform 0.3s ease;
        
        &.rotating {
            animation: rotate 1s linear infinite;
        }
    }
    
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
}

@keyframes rotate {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}

.kanban-board-wrapper {
    flex: 1;
    overflow-x: auto;
    overflow-y: hidden;
    
    &::-webkit-scrollbar {
        height: 10px;
    }
    
    &::-webkit-scrollbar-track {
        background: #f3f4f6;
        border-radius: 5px;
    }
    
    &::-webkit-scrollbar-thumb {
        background: #d1d5db;
        border-radius: 5px;
        border: 2px solid #f3f4f6;
        
        &:hover {
            background: #9ca3af;
        }
    }
}

.kanban-board {
    display: flex;
    gap: 16px;
    padding: 24px;
    height: 100%;
}

.loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.9);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.spinner-large {
    width: 48px;
    height: 48px;
    border: 4px solid #e5e7eb;
    border-top-color: #3b82f6;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

.loading-text {
    margin-top: 16px;
    font-size: 14px;
    color: #6b7280;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}
</style>

